import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

import { decodeQRImage, normalizeTOTPSecret } from './totpSecret';

interface Props {
  // Current stored value — canonical no-spaces base32. Empty when not set.
  value: string;
  // Called with the new canonical base32 secret (or empty string to clear).
  onChange: (secret: string) => void;
}

// TOTPSecretInput accepts a TOTP/2FA secret from the operator in either
// shape: pasted base32 (with or without spaces) or an uploaded QR-code
// image (decoded client-side via jsQR → otpauth:// URI). In both cases
// the parent only ever sees a canonical no-spaces base32 string, which
// goes into the credential blob under TOTP_SECRET_INPUT_KEY.
export function TOTPSecretInput({ value, onChange }: Props) {
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    setDraft(raw);
    if (raw.trim() === '') {
      setError(null);
      onChange('');
      return;
    }
    try {
      const { secret } = normalizeTOTPSecret(raw);
      setError(null);
      onChange(secret);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'invalid secret');
    }
  };

  const onPickFile = async (file: File) => {
    setError(null);
    try {
      const payload = await decodeQRImage(file);
      commit(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to read QR code');
    }
  };

  return (
    <div>
      <label className="text-chariot-text-primary mb-2 block text-sm font-medium">
        TOTP / 2FA secret (optional)
      </label>
      <p className="text-chariot-text-secondary mb-2 text-sm">
        Paste the base32 secret from your authenticator app, paste an{' '}
        <code>otpauth://</code> URI, or upload a QR-code image. The recorder
        derives a fresh 6-digit code at replay time — the code itself is never
        stored.
      </p>
      <div className="flex gap-2">
        <input
          type="password"
          placeholder="JBSW Y3DP EHPK 3PXP   or   otpauth://totp/..."
          value={draft}
          onChange={e => commit(e.target.value)}
          className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-[2px] border p-1.5 pl-3"
          data-testid="totp-secret-input"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => fileRef.current?.click()}
          aria-label="Upload QR code image"
          data-testid="totp-secret-upload"
        >
          <ArrowUpTrayIcon className="size-4" />
          Upload QR
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          aria-label="Upload QR code image"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) void onPickFile(file);
            // Reset so picking the same file twice re-fires the handler.
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <p className="text-chariot-status-critical mt-1 text-xs">{error}</p>
      )}
      {!error && value && (
        <p className="text-chariot-text-secondary mt-1 text-xs">
          Secret stored ({value.length} chars). Will be used at replay time.
        </p>
      )}
    </div>
  );
}
